import {
  Box,
  SxProps,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import { Card } from "src/design";
import {
  OfferPerformanceRow,
  OfferPerformanceTotals,
} from "src/data/admin/types/analytics";
import {
  colorBackgroundInfo,
  colorBorder,
  colorSurface,
  colorTextDisabled,
  colorTextError,
  colorTextInfo,
  colorTextMuted,
  colorTextPrimary,
  colorTextSecondary,
  colorTextSuccess,
  iconColorInfo,
  iconColorPink,
  iconColorPurple,
  iconColorSuccess,
  colorDealAccent,
  colorHeatmapBaseRgb,
} from "src/design/tokens";

// Heatmap fill for a weekday cell: empty cells stay transparent; otherwise the
// blue deepens with the cover count relative to the busiest cell in the table.
const heatmapBackground = (value: number, max: number): string => {
  if (value <= 0 || max <= 0) return "transparent";
  const intensity = value / max;
  // Floor keeps low-but-present cells visible; cap stays readable under text.
  const alpha = 0.12 + intensity * 0.73;
  return `rgba(${colorHeatmapBaseRgb}, ${alpha.toFixed(2)})`;
};

// Above this intensity the blue is dark enough that white text reads better.
const HEATMAP_DARK_TEXT_THRESHOLD = 0.45;

// Weekday columns, Monday-first — matches the `coversByWeekday` buckets the
// backend returns for each offer row.
const WEEKDAY_KEYS = [
  "mon",
  "tue",
  "wed",
  "thu",
  "fri",
  "sat",
  "sun",
] as const;

// Cycled per row so each offer gets a distinct colour dot, like the mock.
const DOT_PALETTE = [
  iconColorInfo,
  iconColorSuccess,
  colorDealAccent,
  iconColorPurple,
  iconColorPink,
];

interface OfferPerformanceTableProps {
  title: string;
  rows: OfferPerformanceRow[];
  totals: OfferPerformanceTotals;
  sx?: SxProps;
}

const headCellSx = {
  color: colorTextMuted,
  fontSize: "0.75rem",
  fontWeight: 500,
  borderBottom: `1px solid ${colorBorder}`,
  py: 1,
};

const numberCellSx = {
  textAlign: "center" as const,
  fontSize: "0.85rem",
  borderBottom: `1px solid ${colorBorder}`,
  py: 1,
};

const DeltaCell = ({
  deltaPercentage,
  isNew,
  newLabel,
}: {
  deltaPercentage: number | null;
  isNew: boolean;
  newLabel: string;
}) => {
  if (isNew) {
    return (
      <Box
        component="span"
        sx={{
          display: "inline-block",
          px: 1,
          py: 0.25,
          borderRadius: 1,
          fontSize: "0.7rem",
          fontWeight: 600,
          backgroundColor: colorBackgroundInfo,
          color: colorTextInfo,
        }}
      >
        {newLabel}
      </Box>
    );
  }
  if (deltaPercentage === null) {
    return <Typography component="span" sx={{ color: colorTextDisabled }}>–</Typography>;
  }
  const positive = deltaPercentage >= 0;
  return (
    <Typography
      component="span"
      sx={{
        fontSize: "0.8rem",
        fontWeight: 600,
        color: positive ? colorTextSuccess : colorTextError,
      }}
    >
      {`${positive ? "+" : ""}${deltaPercentage} %`}
    </Typography>
  );
};

const Covers = ({ value, onDarkFill }: { value: number; onDarkFill?: boolean }) =>
  value > 0 ? (
    <Typography
      component="span"
      sx={{
        color: onDarkFill ? colorSurface : colorTextPrimary,
        fontSize: "0.85rem",
        fontWeight: onDarkFill ? 600 : 400,
      }}
    >
      {value}
    </Typography>
  ) : (
    <Typography component="span" sx={{ color: colorTextDisabled }}>–</Typography>
  );

export const OfferPerformanceTable = ({
  title,
  rows,
  totals,
  sx,
}: OfferPerformanceTableProps) => {
  const { t } = useTranslation();
  const newLabel = t("analytics.offerPerformance.newBadge");

  // Busiest single weekday cell across every offer — the heatmap's upper bound.
  const maxCover = rows.reduce(
    (max, row) => Math.max(max, ...row.coversByWeekday),
    0,
  );

  return (
    <Card sx={{ flexDirection: "column", gap: 1.5, width: "100%", ...sx }}>
      <Typography
        sx={{ fontSize: "1rem", fontWeight: 600, color: colorTextPrimary }}
      >
        {title}
      </Typography>

      <Box sx={{ width: "100%", overflowX: "auto" }}>
        <Table size="small" sx={{ minWidth: 640 }}>
          <TableHead>
            <TableRow>
              <TableCell sx={{ ...headCellSx, textAlign: "left" }}>
                {t("analytics.offerPerformance.offerColumn")}
              </TableCell>
              {WEEKDAY_KEYS.map((key) => (
                <TableCell key={key} sx={{ ...headCellSx, textAlign: "center" }}>
                  {t(`analytics.offerPerformance.weekdays.${key}`)}
                </TableCell>
              ))}
              <TableCell sx={{ ...headCellSx, textAlign: "center" }}>
                {t("analytics.offerPerformance.weekTotal")}
              </TableCell>
              <TableCell sx={{ ...headCellSx, textAlign: "center" }}>Δ</TableCell>
            </TableRow>
          </TableHead>

          <TableBody>
            {rows.map((row, index) => (
              <TableRow key={row.label}>
                <TableCell
                  sx={{
                    ...numberCellSx,
                    textAlign: "left",
                    color: colorTextSecondary,
                  }}
                >
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <Box
                      sx={{
                        width: 10,
                        height: 10,
                        borderRadius: "3px",
                        flexShrink: 0,
                        backgroundColor:
                          DOT_PALETTE[index % DOT_PALETTE.length],
                      }}
                    />
                    {row.label}
                  </Box>
                </TableCell>
                {row.coversByWeekday.map((covers, dayIndex) => {
                  const onDarkFill =
                    maxCover > 0 && covers / maxCover > HEATMAP_DARK_TEXT_THRESHOLD;
                  return (
                    <TableCell
                      key={WEEKDAY_KEYS[dayIndex]}
                      sx={{
                        ...numberCellSx,
                        backgroundColor: heatmapBackground(covers, maxCover),
                      }}
                    >
                      <Covers value={covers} onDarkFill={onDarkFill} />
                    </TableCell>
                  );
                })}
                <TableCell
                  sx={{ ...numberCellSx, fontWeight: 600, color: colorTextPrimary }}
                >
                  {row.total}
                </TableCell>
                <TableCell sx={numberCellSx}>
                  <DeltaCell
                    deltaPercentage={row.deltaPercentage}
                    isNew={row.isNew}
                    newLabel={newLabel}
                  />
                </TableCell>
              </TableRow>
            ))}

            <TableRow>
              <TableCell
                sx={{
                  ...numberCellSx,
                  textAlign: "left",
                  fontWeight: 600,
                  color: colorTextPrimary,
                  borderBottom: "none",
                }}
              >
                {t("analytics.offerPerformance.totalRow")}
              </TableCell>
              {totals.coversByWeekday.map((covers, dayIndex) => (
                <TableCell
                  key={WEEKDAY_KEYS[dayIndex]}
                  sx={{
                    ...numberCellSx,
                    fontWeight: 600,
                    color: colorTextPrimary,
                    borderBottom: "none",
                  }}
                >
                  {covers}
                </TableCell>
              ))}
              <TableCell
                sx={{
                  ...numberCellSx,
                  fontWeight: 700,
                  color: colorTextPrimary,
                  borderBottom: "none",
                }}
              >
                {totals.total}
              </TableCell>
              <TableCell sx={{ ...numberCellSx, borderBottom: "none" }}>
                <DeltaCell
                  deltaPercentage={totals.deltaPercentage}
                  isNew={totals.isNew}
                  newLabel={newLabel}
                />
              </TableCell>
            </TableRow>
          </TableBody>
        </Table>
      </Box>
    </Card>
  );
};
